import { useTheme } from "@/hooks/useTheme";

const features = [
  {
    icon: "ri-radar-line",
    tag: "01",
    title: "Instant Lead Capture",
    description: "Capture every inbound opportunity across calls, forms, and messages — nothing slips through the cracks. Your system works around the clock so you never miss a signal.",
    stat: { value: "100%", label: "Capture rate" },
    image: "https://public.readdy.ai/ai/img_res/cd705e553afe8879c4d485fc72b6e31d.jpg",
    imageAlt: "Instant lead capture visualization",
    flip: false,
  },
  {
    icon: "ri-message-3-line",
    tag: "02",
    title: "Automated Response",
    description: "Engage leads instantly with voice, SMS, and chat automation. Your system responds in seconds — 24/7 — before your competitors even see the inquiry come in.",
    stat: { value: "<60s", label: "Response time" },
    image: "https://readdy.ai/api/search-image?query=sleek%20automated%20messaging%20interface%20showing%20real-time%20SMS%20voice%20and%20chat%20responses%20flowing%20simultaneously%2C%20clean%20UI%20interface%20with%20sage%20green%20accent%20lights%20on%20dark%20violet%20background%2C%20instant%20communication%20system%20concept%20for%20enterprise%20automation%2C%20premium%20minimal%20design%20with%20data%20pulse%20effects&width=900&height=660&seq=sol_resp_v2&orientation=landscape",
    imageAlt: "Automated response system",
    flip: true,
  },
  {
    icon: "ri-filter-3-line",
    tag: "03",
    title: "Smart Qualification",
    description: "Filter and prioritize high-value opportunities automatically using intelligent scoring. Your team focuses only on leads that are ready to convert — not wasted effort.",
    stat: { value: "3×", label: "Team efficiency" },
    image: "https://readdy.ai/api/search-image?query=intelligent%20AI%20scoring%20and%20filtering%20funnel%20visualization%20with%20high-value%20leads%20highlighted%20and%20low-quality%20signals%20removed%2C%20abstract%20data%20qualification%20system%20with%20glowing%20priority%20indicators%2C%20deep%20violet%20and%20ivory%20color%20palette%2C%20enterprise%20artificial%20intelligence%20operational%20concept%2C%20minimal%20clean%20aesthetic&width=900&height=660&seq=sol_qual_v2&orientation=landscape",
    imageAlt: "Smart lead qualification",
    flip: false,
  },
  {
    icon: "ri-route-line",
    tag: "04",
    title: "Intelligent Routing",
    description: "Send leads to the right person at the right time without delays or manual handoffs. Every opportunity arrives with full context — ready to close.",
    stat: { value: "0", label: "Missed handoffs" },
    image: "https://readdy.ai/api/search-image?query=sophisticated%20network%20routing%20diagram%20with%20flowing%20lead%20paths%20connecting%20to%20multiple%20team%20nodes%2C%20clean%20geometric%20node%20graph%20with%20glowing%20connection%20lines%20in%20sage%20green%20on%20dark%20background%2C%20enterprise%20workflow%20routing%20visualization%20showing%20intelligent%20lead%20distribution%20system%2C%20abstract%20minimal%20professional%20design&width=900&height=660&seq=sol_route_v2&orientation=landscape",
    imageAlt: "Intelligent routing network",
    flip: true,
  },
];

export default function SolutionSection() {
  const { isDark } = useTheme();

  const bg = isDark ? "#0d0818" : "white";
  const bgAlt = isDark ? "#120720" : "#faf8f5";
  const headingColor = isDark ? "#e4d0f8" : "#4d316c";
  const subheadingColor = isDark ? "#c4aad8" : "#705a89";
  const bodyColor = isDark ? "rgba(255,255,255,0.58)" : "#7a6b80";
  const labelColor = isDark ? "#a9baab" : "#869b8b";
  const iconBg = isDark ? "rgba(169,186,171,0.12)" : "#f0e9de";
  const rowBorder = isDark ? "rgba(255,255,255,0.06)" : "#f0e9de";
  const statBg = isDark ? "rgba(255,255,255,0.06)" : "#f0e9de";
  const statBorder = isDark ? "rgba(255,255,255,0.1)" : "#e4d8c8";
  const pillAltBg = isDark ? "rgba(255,255,255,0.1)" : "#f0e9de";
  const pillAltText = isDark ? "rgba(255,255,255,0.8)" : "#4d316c";

  return (
    <section id="solution" className="overflow-hidden transition-colors duration-300" style={{ background: bg }}>
      {/* Header */}
      <div className="py-28 md:py-36 text-center max-w-4xl mx-auto px-6 md:px-10">
        <span className="inline-block text-xs font-semibold tracking-widest uppercase mb-5" style={{ color: labelColor }}>
          The Patronum System
        </span>
        <h2 className="text-4xl md:text-6xl lg:text-7xl font-semibold leading-tight tracking-tight mb-6" style={{ color: headingColor }}>
          Built for Control.
          <br />
          <span style={{ color: subheadingColor }}>Designed for Performance.</span>
        </h2>
        <p className="text-base md:text-lg leading-relaxed max-w-2xl mx-auto" style={{ color: bodyColor }}>
          Four integrated intelligence layers working in concert to convert every lead into a tracked, nurtured, and closed opportunity.
        </p>

        {/* Flow pill */}
        <div className="mt-10 flex items-center justify-center gap-2 flex-wrap">
          {["Capture", "Respond", "Qualify", "Convert"].map((step, i) => (
            <div key={step} className="flex items-center gap-2">
              <span
                className="px-5 py-2 rounded-full text-sm font-medium"
                style={{
                  background: i % 2 === 0 ? "#4d316c" : pillAltBg,
                  color: i % 2 === 0 ? "white" : pillAltText,
                }}
              >
                {step}
              </span>
              {i < 3 && (
                <i className="ri-arrow-right-line text-sm" style={{ color: "#a9baab" }}></i>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Alternating feature rows */}
      {features.map((feat, idx) => (
        <div
          key={feat.tag}
          className={`flex flex-col ${feat.flip ? "lg:flex-row-reverse" : "lg:flex-row"} items-stretch min-h-[500px]`}
          style={{ borderTop: `1px solid ${rowBorder}` }}
        >
          {/* Image side */}
          <div className="w-full lg:w-[55%] relative overflow-hidden" style={{ minHeight: "380px" }}>
            <img
              src={feat.image}
              alt={feat.imageAlt}
              className="w-full h-full object-cover object-center"
              style={{ minHeight: "380px" }}
            />
            <div className={`absolute inset-0 bg-gradient-to-${feat.flip ? "r" : "l"} from-transparent to-black/5`} />
          </div>

          {/* Content side */}
          <div
            className="w-full lg:w-[45%] flex flex-col justify-center px-10 md:px-16 py-16 md:py-24 transition-colors duration-300"
            style={{ background: idx % 2 === 0 ? bg : bgAlt }}
          >
            {/* Tag + icon */}
            <div className="flex items-center gap-3 mb-6">
              <span className="text-xs font-bold tracking-widest" style={{ color: "#a9baab" }}>
                {feat.tag}
              </span>
              <div className="w-9 h-9 flex items-center justify-center rounded-xl" style={{ background: iconBg }}>
                <i className={`${feat.icon} text-base`} style={{ color: isDark ? "#c4aad8" : "#4d316c" }}></i>
              </div>
            </div>

            <h3 className="text-2xl md:text-4xl font-semibold tracking-tight leading-tight mb-5" style={{ color: headingColor }}>
              {feat.title}
            </h3>
            <p className="text-sm md:text-base leading-relaxed mb-8" style={{ color: bodyColor }}>
              {feat.description}
            </p>

            {/* Stat callout */}
            <div
              className="inline-flex items-center gap-4 px-6 py-4 rounded-2xl self-start"
              style={{ background: statBg, border: `1px solid ${statBorder}` }}
            >
              <span className="text-2xl font-semibold tracking-tight" style={{ color: headingColor }}>
                {feat.stat.value}
              </span>
              <span className="text-xs" style={{ color: labelColor }}>{feat.stat.label}</span>
            </div>
          </div>
        </div>
      ))}
    </section>
  );
}
